/* eslint-disable max-depth */
import { jupiterFetch } from '@svenskaspelab/nova-core/jupiter';

import type { ITSProductId } from '../@types/Product.js';
import { CancelWagerError } from '../error/index.js';
import { type ErrorResponse, isErrorResponse } from '../service/service-wager/utils/types.js';
import type { CancelWager } from './Cancel.js';
import { type CancelContextApi } from './context/createCancelContextApi.js';

export type DTOResponse = {
  serialNum?: string;
  CancelWagerReply?: {
    serial?: string;
    betAmount?: string;
  };
  wagerAmounts?: {
    totalAmount?: string;
  };
  products?: ITSProductId[];
  accountBalance?: string;
  cancelTime: string;
};

/**
 * Executes the cancel request for the given wagers to cancel.
 *
 * @param contextApi - The cancelation context api
 * @param wagersToCancel - Array with wagers to cancel
 * @returns The number of successfully canceled wagers
 */
export async function executeCancelRequest(
  contextApi: CancelContextApi,
  wagersToCancel: CancelWager[]
): Promise<number> {
  const cancelItems = contextApi.getCancelItems();
  let successCount = 0;

  for (const wager of wagersToCancel) {
    try {
      const url = wager.isMarketplace
        ? `/marketplace/1/wagers/${wager.transactionId}`
        : `/wager/5/wagers/${wager.transactionId}`;

      // eslint-disable-next-line no-await-in-loop
      const response = await jupiterFetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        credentials: 'include', // behövs dessa?
        cache: 'no-store', // behövs dessa?
      });

      // eslint-disable-next-line no-await-in-loop
      const data = (await response.json()) as DTOResponse | ErrorResponse;

      if (!response.ok || isErrorResponse(data)) {
        let code = '';
        let message = 'Unknown error';
        if (isErrorResponse(data)) {
          code = data.error.code;
          message = data.error.message ?? 'Unknown error';
        }
        const error = new CancelWagerError({
          clientCode: 'CANCEL_PAYMENT_FAILURE',
          code,
          extraData: { message },
        });
        cancelItems.set(wager, { error });
        break;
      }
      successCount += 1;
      cancelItems.set(wager, { data });
      // publishEvent(eventNames.PAYMENT_CANCEL, { detail: { wager, dto: data } });
    } catch (error_) {
      if (error_ instanceof Error && 'code' in error_ && typeof error_.code === 'string') {
        switch (error_.name) {
          case 'TypeError': {
            const error = new CancelWagerError({
              clientCode: 'CANCEL_PAYMENT_FAILURE',
              code: error_.code,
              extraData: { message: error_.message },
            });
            cancelItems.set(wager, { error });
            break;
          }
          default:
            cancelItems.set(wager, { error: error_ });
        }
      }
    }
  }

  return successCount;
}
